import { ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import type { PlaneView } from '~/engine/view/views/PlaneView';

/** A line being drawn point by point, in patient mm. */
export interface LineInProgress {
    pointsMm: readonly ReadonlyVec3[];
    /** Where the pointer is, or null when it has not moved since the last point. */
    hoverPointMm: ReadonlyVec3 | null;
    /** True when a press or release at the hover point would close the line. */
    closing: boolean;
}

const START_DOT_RADIUS_PX = 3;
const LINE_WIDTH_PX = 1;
const CLOSING_LINE_WIDTH_PX = 2;

/** Paints a kept drawing without clearing, leaving the context state as it found it. */
export type OverlayPainter = () => void;

const keptPainters = new WeakMap<HTMLCanvasElement, Set<OverlayPainter>>();

/** Calls `paint` after every {@link clearOverlayCanvas} of `canvas`. Returns the release. */
export function keepOnOverlayCanvas(
    canvas: HTMLCanvasElement,
    paint: OverlayPainter
): () => void {
    let painters = keptPainters.get(canvas);
    if (!painters) {
        painters = new Set();
        keptPainters.set(canvas, painters);
    }
    painters.add(paint);
    return () => {
        keptPainters.get(canvas)?.delete(paint);
    };
}

/** Clears the canvas, then repaints every drawing kept on it except `own`. */
export function clearOverlayCanvas(
    ctx: CanvasRenderingContext2D,
    own?: OverlayPainter
): void {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    for (const paint of [...(keptPainters.get(ctx.canvas) ?? [])]) {
        if (paint !== own) {
            paint();
        }
    }
}

/** Sync a 2D canvas's drawing buffer to its current CSS layout size. */
export function syncCanvasSizeToLayout(ctx: CanvasRenderingContext2D): void {
    const { canvas } = ctx;
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
    }
}

/** Adds the polyline through the points to the current path and returns its first point in canvas pixels. */
export function tracePolyline(
    ctx: CanvasRenderingContext2D,
    view: Pick<PlaneView, 'canvasCoordsFromPatientCoords'>,
    pointsMm: readonly ReadonlyVec3[]
): ReadonlyVec2 {
    const first = view.canvasCoordsFromPatientCoords(pointsMm[0]);
    ctx.moveTo(first[0], first[1]);
    for (let i = 1; i < pointsMm.length; i++) {
        const p = view.canvasCoordsFromPatientCoords(pointsMm[i]);
        ctx.lineTo(p[0], p[1]);
    }
    return first;
}

/** Strokes a line in progress as a solid polyline through its points to the hover point, wider while closing, with a dot on its first point. */
export function strokeLineInProgress(
    ctx: CanvasRenderingContext2D,
    view: PlaneView,
    line: LineInProgress,
    strokeStyle: string
): void {
    if (line.pointsMm.length === 0) {
        return;
    }
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = line.closing ? CLOSING_LINE_WIDTH_PX : LINE_WIDTH_PX;
    ctx.setLineDash([]);
    ctx.beginPath();
    const first = tracePolyline(ctx, view, line.pointsMm);
    if (line.hoverPointMm) {
        const hover = view.canvasCoordsFromPatientCoords(line.hoverPointMm);
        ctx.lineTo(hover[0], hover[1]);
    }
    ctx.stroke();
    // The start dot is drawn last so the polyline cannot hide the point the user aims at to close.
    ctx.beginPath();
    ctx.arc(first[0], first[1], START_DOT_RADIUS_PX, 0, 2 * Math.PI);
    ctx.stroke();
}
